import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SUPPORT_EMAIL, PRIVACY_EMAIL, COMPANY_NAME, WEBSITE_URL } from '../constants/contact'

export default function Privacy() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            goparkingnow
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> December 7, 2025<br />
              <strong>Last Updated:</strong> December 7, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                SAS NEXIA MOBILE ("we", "us", or "our") operates goparkingnow.com (the "Service"). 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
                visit our website and use our service.
              </p>
              <p className="text-gray-700">
                By using our Service, you consent to the data practices described in this Privacy Policy. 
                If you do not agree with the practices described in this policy, please do not use our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">We may collect the following personal information:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Full name</li>
                <li>Email address</li>
                <li>Payment information (processed securely through our payment processor)</li>
                <li>Account credentials (encrypted passwords)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Usage Information</h3>
              <p className="text-gray-700 mb-4">We automatically collect certain information when you use our Service:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Location data (when you search for parking spots)</li>
                <li>Device information (browser type, operating system)</li>
                <li>Usage patterns and preferences</li>
                <li>IP address and general location information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>To provide and maintain our parking spot finder service</li>
                <li>To process your subscription and payments</li>
                <li>To personalize your experience and improve our service</li>
                <li>To communicate with you about your account and service updates</li>
                <li>To provide customer support</li>
                <li>To detect and prevent fraud or unauthorized access</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share your information with trusted third-party service providers who assist us in operating our Service, 
                such as payment processors, hosting providers, and analytics services.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to valid legal requests, 
                such as court orders or government investigations.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                as part of the business transaction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure payment processing through certified providers</li>
              </ul>
              <p className="text-gray-700">
                However, no method of transmission over the internet or electronic storage is 100% secure, 
                and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Access:</strong> Request access to your personal information we hold</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, please contact us using the information provided in the Contact section below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our Service. 
                Cookies help us remember your preferences, analyze usage patterns, and provide personalized content.
              </p>
              <p className="text-gray-700">
                You can control cookie settings through your browser preferences, but disabling cookies may 
                affect the functionality of our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal information for as long as necessary to provide our Service and fulfill 
                the purposes outlined in this Privacy Policy. When you cancel your subscription, we may retain 
                certain information for legal, regulatory, or legitimate business purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our Service is not intended for children under the age of 13. We do not knowingly collect 
                personal information from children under 13. If we become aware that we have collected 
                personal information from a child under 13, we will take steps to delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                Your continued use of the Service after such modifications constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{COMPANY_NAME}</strong><br />
                  Website: {WEBSITE_URL}<br />
                  Email: <a href={`mailto:${PRIVACY_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{PRIVACY_EMAIL}</a><br />
                  Support: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{SUPPORT_EMAIL}</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
